import { useCallback } from "react"
import { GraphQLTaggedNode, useMutation, UseMutationConfig } from "react-relay"
import { MutationParameters, PayloadError } from "relay-runtime"
import { extractGraphqlErrors, GraphqlErrors } from "@/lib/utils"

// The purpose of this custom mutation hook is to execute the mutation with enhanced error handling
export function useCustomMutation<T extends MutationParameters>(mutation: GraphQLTaggedNode) {
  const [commit, isInFlight] = useMutation<T>(mutation)

  const customCommit = useCallback(
    (config: UseMutationConfig<T> & { onMutationResultError?: (errors: GraphqlErrors) => void }) => {
      const configWithHandling = {
        ...config,
        onCompleted: (response: T["response"], graphqlErrors: PayloadError[] | null) => {
          if (graphqlErrors?.length) {
            // Call original onError if exists
            config.onError?.(new Error(graphqlErrors[0].message))
            return
          }

          const errors = extractGraphqlErrors(response)
          if (errors.length > 0) {
            // Call original onMutationError if exists
            config.onMutationResultError?.(errors)
            return
          }

          // Call original onCompleted if exists
          config.onCompleted?.(response, graphqlErrors)
        },
        onError: (error: Error) => {
          // Call original onError if exists
          config.onError?.(error)
        },
      }

      commit(configWithHandling)
    },
    [commit],
  )

  return [customCommit, isInFlight] as const
}
